import { useEffect, useState } from 'react';
import { X, Check, Mail, Car, MapPin, MessageSquare, Home as HomeIcon, Building2 } from 'lucide-react';
import { submitQuoteRequest } from '../lib/api';
import { track } from '../lib/analytics';
import { useLang } from '../context/LanguageContext';

const EMPTY = {
  name: '',
  phone: '',
  email: '',
  vehicle: '',
  license_plate: '',
  treatment: '',
  location: 'Aan huis',
  notes: '',
};

const OfferteModal = ({ open, onClose, prefill = {} }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const { ui, lang } = useLang();
  const m = ui.modal;

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        ...(prefill.treatment ? { treatment: prefill.treatment } : {}),
        ...(prefill.notes ? { notes: prefill.notes } : {}),
      });
      setSent(false);
      setError('');
    }
  }, [open, prefill]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await submitQuoteRequest(form);
      track('quote_submitted', { treatment: form.treatment || 'onbekend', location: form.location, language: lang });
      setSent(true);
      setTimeout(() => { onClose(); }, 2500);
    } catch (err) {
      setError(m.error);
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start md:items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="offerte-modal"
    >
      <div
        className="relative w-full max-w-2xl bg-[#111114] border border-white/10 rounded-2xl shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Sluiten"
          data-testid="offerte-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 md:p-10">
          <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase mb-3">
            <Mail className="w-3.5 h-3.5" /> {m.eyebrow}
          </div>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">
            {m.title}
          </h2>
          <p className="mt-3 text-white/60 text-sm">
            {m.subtitle}
          </p>

          {sent ? (
            <div className="mt-10 py-10 text-center" data-testid="offerte-modal-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center mb-5">
                <Check className="w-6 h-6" />
              </div>
              <div className="text-xl text-white font-medium">{m.successTitle}</div>
              <p className="mt-2 text-white/60 text-sm max-w-md mx-auto">
                {m.successText}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="offerte-modal-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={m.name} testid="of-name">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    className={inputCls}
                    data-testid="of-name-input"
                  />
                </Field>
                <Field label={m.phone} testid="of-phone">
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    className={inputCls}
                    data-testid="of-phone-input"
                  />
                </Field>
              </div>

              <Field label={m.email} testid="of-email">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className={inputCls}
                  data-testid="of-email-input"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={m.vehicle} icon={Car} testid="of-vehicle">
                  <input
                    required
                    placeholder={m.vehiclePh}
                    value={form.vehicle}
                    onChange={(e) => set('vehicle', e.target.value)}
                    className={inputCls}
                    data-testid="of-vehicle-input"
                  />
                </Field>
                <Field label={m.plate} testid="of-plate">
                  <input
                    value={form.license_plate}
                    onChange={(e) => set('license_plate', e.target.value.toUpperCase())}
                    className={inputCls}
                    placeholder="XX-000-X"
                    data-testid="of-plate-input"
                  />
                </Field>
              </div>

              <Field label={m.treatment} testid="of-treatment">
                <input
                  required
                  placeholder={m.treatmentPh}
                  value={form.treatment}
                  onChange={(e) => set('treatment', e.target.value)}
                  className={inputCls}
                  data-testid="of-treatment-input"
                />
              </Field>

              <Field label={m.location} icon={MapPin} testid="of-location">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'Aan huis', icon: HomeIcon, label: m.locHome },
                    { id: 'Shiny Cleaning locatie', icon: Building2, label: m.locShop },
                  ].map(opt => {
                    const active = form.location === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => set('location', opt.id)}
                        data-testid={`of-location-${opt.id.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-2 px-4 py-3 rounded-md border text-sm transition-colors ${
                          active
                            ? 'bg-[#2f6bff] border-[#2f6bff] text-white'
                            : 'bg-white/[0.02] border-white/10 text-white/80 hover:border-white/25'
                        }`}
                      >
                        <opt.icon className="w-4 h-4" /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label={m.notes} icon={MessageSquare} testid="of-notes">
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder={m.notesPh}
                  className={`${inputCls} resize-none`}
                  data-testid="of-notes-input"
                />
              </Field>

              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="of-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] disabled:opacity-60 text-white text-sm font-medium transition-colors"
              >
                {loading ? m.submitting : m.submit}
              </button>

              <p className="text-white/40 text-xs text-center">
                {m.disclaimer}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const inputCls =
  'w-full bg-white/[0.03] border border-white/10 rounded-md px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2f6bff] transition-colors';

const Field = ({ label, icon: Icon, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-white/60 text-[11px] tracking-[0.2em] uppercase mb-2 flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </span>
    {children}
  </label>
);

export default OfferteModal;
